/* eslint-disable @typescript-eslint/ban-ts-comment */
import createImagePlugin from '@draft-js-plugins/image';
import {LetrusApi} from '@letrustech/letrus-api-interfaces';
import {CompositionAid} from '@letrustech/letrus-api-interfaces/dist/interfaces/letrus_api';
import {CompositionProps} from 'components/StatisticsSidebar';
import Editor from 'draft-js-plugins-editor';
import useCompletedTestReport from 'features/useCompletedTestReport';
import {useEffect, useRef, useState} from 'react';
import useDraftAsTextHighlighter from 'utils/hooks/useDraftAsTextHighlighter/useDraftAsTextHighlighter';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {WritePreparation} from 'APIs/letrusAPI/writePreparation/types';
import styles from './AsideProposal.module.scss';
import ProposalAid from './ProposalAid';

export interface AsideProposalProps {
  composition?: ImmutableMap<CompositionProps>;
  isLoading?: boolean;
  unsetOverflow?: boolean;
  writePreparation?: WritePreparation;
  setHasScrolledToEnd?(scrolled: boolean): void;
}

function AsideProposal({
  composition,
  isLoading,
  unsetOverflow,
  writePreparation,
  setHasScrolledToEnd,
}: AsideProposalProps): JSX.Element {
  const {instructionTitle, compositionGenre, aids} = useCompletedTestReport({
    compositionDetails: composition,
  });
  const scrollRef = useRef<HTMLElement>(null);
  const [scrollPosition, setScrollPosition] = useState();
  const [localWritePreparation, setLocalWritePreparation] =
    useState<WritePreparation>();
  const writePreparationIDB = useIndexedDB<WritePreparation>(
    'LocalWritePreparations',
  );

  const {trackDocumentEvent} = useTrackEvent();

  const eventData: EventData = {
    learningPathUserInstance: {
      learning_path_user_instance_id: Number(
        composition?.get('learning_path_user_instance_id'),
      ),
      step_id: composition?.getIn(['step', 'id']) ?? 0,
    },
    composition: {
      composition_id: composition?.get('id'),
    },
    test: {
      test_id: Number(composition?.getIn(['test', 'id'])),
    },
  };

  const draftHighlighter = useDraftAsTextHighlighter();

  useEffect(() => {
    trackDocumentEvent({
      eventName: 'selected_proposal_text',
      eventType: 'onselectionchange',
      eventCallback: () => {
        const userSelection = document.getSelection();

        if (userSelection?.toString().length) {
          const proposal = document.querySelector('[data-proposal]');

          if (proposal?.contains(userSelection.focusNode)) {
            return {
              ...eventData,
              supportingContent: {
                selected_text: userSelection.toString() || '',
              },
            };
          }
        }
        return null;
      },
    });

    writePreparationIDB
      .getItem(String(composition?.get('id')))
      .then((localWritePreparation) => {
        if (localWritePreparation) {
          setLocalWritePreparation(localWritePreparation);
        }
      });
  }, []);

  useEffect(() => {
    if (writePreparation || localWritePreparation) {
      draftHighlighter.convertHTMLStringToDraftState(
        localWritePreparation?.aids_markup_html ??
          writePreparation?.aids_markup_html ??
          '',
      );
    }
  }, [writePreparation, localWritePreparation]);

  useEffect(() => {
    updateScroll();
  }, [isLoading]);

  function updateScroll() {
    setTimeout(() => {
      if (scrollRef?.current) {
        scrollRef.current.scrollTop = scrollPosition || 0;
      }
    }, 10);
  }

  if (isLoading) {
    return <p>Carregando...</p>;
  }

  function onScroll(e) {
    const sidebar = e.target;
    if (sidebar.scrollHeight - sidebar.scrollTop === sidebar.clientHeight) {
      setHasScrolledToEnd?.(true);
    }
    setScrollPosition(e.target.scrollTop);
  }

  function sortAids(aid: CompositionAid[] | undefined) {
    return aid?.sort(
      (aidA: CompositionAid, aidB: CompositionAid) => aidA.order - aidB.order,
    );
  }

  return (
    <section
      className={`${styles.asideProposal} ${
        unsetOverflow && styles.unsetOverflow
      }`}
      onScroll={onScroll}
      ref={scrollRef}
    >
      {!writePreparation &&
        sortAids(aids)?.map((aid: LetrusApi.CompositionAid) => (
          <ProposalAid key={Number(aid.id)} aid={aid} />
        ))}

      <section data-proposal>
        {writePreparation || localWritePreparation ? (
          <>
            <Editor
              // @ts-ignore
              editorState={draftHighlighter.editorState}
              customStyleMap={draftHighlighter.styleMap}
              blockRenderMap={draftHighlighter.blockRenderMap}
              plugins={[createImagePlugin()]}
              onChange={() => {}} // Draft understands Editor as a controlled input, therefore onChange is needed to avoid errors
              readOnly
            />
          </>
        ) : (
          <>
            <header className={styles.proposalHeader}>
              <h3>Proposta</h3>

              <hgroup className={styles.themeContainer}>
                <h4>
                  <strong>{`Tema: ${instructionTitle}`}</strong>
                </h4>

                <h5>{`Gênero: ${compositionGenre}`}</h5>
              </hgroup>
            </header>

            {composition?.size && (
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    composition?.getIn(['instruction_id', 'content_html']) ??
                    '',
                }}
              />
            )}
          </>
        )}
      </section>
    </section>
  );
}

export default AsideProposal;
